// Map overlays for the solar widget (docs/12-solar-roof-scan.md §7.4, §5.7, §7.7).
//
// Everything drawn on the 2D satellite map lives here: the house pin, the building
// outline (draggable), the terrace trim (dimmed ends, slice, two handles), the rotate
// handle and its stalk, the scan sweep, and one polygon per panel.
//
// Rules that fixed real bugs in the prototype, kept exactly:
//   • Map overlays can't read CSS variables, so colours come in from the branding at
//     runtime (setColors) and everything is redrawn when the branding changes.
//   • The rotate handle is an HTML OverlayView in overlayMouseTarget whose pointerdown
//     listener is added in the CAPTURE phase BEFORE preventMapHitsAndGesturesFrom.
//     A draggable Circle, a Circle with mousedown, or a listener added after Google's
//     all failed (the map stole the gesture).
//   • Trim handles are draggable Circles; their redraw puts them back on the centre
//     line with a re-entry guard so center_changed doesn't call itself.
//   • Panel polygons use fixed colours: they depict real panels, not the brand.
//
// The pin and rotate handle are HTML elements appended into Google's panes, which
// sit inside the widget's shadow root: their CSS is in SOLAR_CSS (.xqs-pin, .xqs-rotate).

/* eslint-disable @typescript-eslint/no-explicit-any */
import { interpolate, offset } from "./geo";
import { outlineCorners, trimCorners, type RowGeometry } from "./handles";
import type { LL, Outline, Trim } from "../types";

/** Scan timing (§5.7): two passes of the sweep, down then back up. */
export const MIN_SCAN_MS = 4200;
const PASS_MS = MIN_SCAN_MS / 2;
/** Panels appear best-first, one every 70 ms (§7.5). */
export const PANEL_REVEAL_MS = 70;

const PANEL_FILL = "#16315E";
const PANEL_STROKE = "#B9D2FF";

export interface OverlayColors {
  accent: string; // outline, slice, scan, pin, rotate handle, handle strokes
  accentText: string; // glyphs on the accent (pin ⌂, rotate ⟳)
}

export interface OverlayEvents {
  /** The outline was dragged and dropped: the new pin is the average of its corners. */
  onOutlineMoved(pin: LL): void;
  /** A trim handle moved (already clamped by the caller's handler via setTrim). */
  onTrimHandle(handle: "t0" | "t1", t: number): void;
  /** The rotate handle moved: the outline's new long-side heading. */
  onRotate(heading: number): void;
}

const easeInOut = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);

/** All overlays on one 2D map. Created once the Maps API has loaded. */
export function createOverlays(g: any, map: any, mapDiv: HTMLElement, colors: OverlayColors, events: OverlayEvents) {
  let c = colors;
  const setVar = (el: HTMLElement) => {
    el.style.setProperty("--xqs-accent", c.accent);
    el.style.setProperty("--xqs-accent-text", c.accentText);
  };

  // ── House pin: an HTML OverlayView in floatPane, pointer-events none ──────
  class Pin extends g.maps.OverlayView {
    div: HTMLDivElement;
    at: LL | null = null;
    constructor() {
      super();
      this.div = document.createElement("div");
      this.div.className = "xqs-pin";
      this.div.innerHTML = "<span>⌂</span>";
      setVar(this.div);
    }
    onAdd() {
      this.getPanes().floatPane.appendChild(this.div);
    }
    draw() {
      if (!this.at) return;
      const p = this.getProjection()?.fromLatLngToDivPixel(new g.maps.LatLng(this.at));
      if (!p) return;
      this.div.style.left = `${p.x}px`;
      this.div.style.top = `${p.y}px`;
    }
    onRemove() {
      this.div.remove();
    }
    place(at: LL, drop: boolean) {
      this.at = at;
      this.div.hidden = false;
      if (drop) {
        // Restart the CSS drop animation (remove, force a reflow, add back)
        this.div.classList.remove("is-dropping");
        void this.div.offsetWidth;
        this.div.classList.add("is-dropping");
      }
      this.draw();
    }
    hide() {
      this.div.hidden = true;
    }
  }

  // ── Rotate handle: HTML OverlayView, capture-phase pointerdown FIRST (§7.4) ──
  class RotateHandle extends g.maps.OverlayView {
    div: HTMLDivElement;
    at: LL | null = null;
    constructor(private onHeading: (h: number) => void, private centre: () => LL | null) {
      super();
      this.div = document.createElement("div");
      this.div.className = "xqs-rotate";
      this.div.textContent = "⟳";
      this.div.title = "Drag to rotate the outline";
      setVar(this.div);
      this.div.addEventListener("pointerdown", (e) => this.onDown(e), { capture: true }); // 1st, capture phase
      g.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div); // 2nd
    }
    onAdd() {
      this.getPanes().overlayMouseTarget.appendChild(this.div);
    }
    draw() {
      if (!this.at) return;
      const p = this.getProjection()?.fromLatLngToDivPixel(new g.maps.LatLng(this.at));
      if (!p) return;
      this.div.style.left = `${p.x}px`;
      this.div.style.top = `${p.y}px`;
    }
    onRemove() {
      this.div.remove();
    }
    place(at: LL) {
      this.at = at;
      this.draw();
    }
    onDown(e: PointerEvent) {
      e.preventDefault();
      e.stopPropagation();
      this.div.setPointerCapture(e.pointerId);
      this.div.classList.add("dragging");
      const move = (ev: PointerEvent) => {
        const cen = this.centre();
        const proj = this.getProjection();
        if (!cen || !proj) return;
        const r = mapDiv.getBoundingClientRect();
        const ll = proj.fromContainerPixelToLatLng(new g.maps.Point(ev.clientX - r.left, ev.clientY - r.top));
        this.onHeading(g.maps.geometry.spherical.computeHeading(new g.maps.LatLng(cen), ll));
      };
      const up = (ev: PointerEvent) => {
        try {
          this.div.releasePointerCapture(ev.pointerId);
        } catch {
          /* already released */
        }
        this.div.classList.remove("dragging");
        this.div.removeEventListener("pointermove", move);
        this.div.removeEventListener("pointerup", up);
        this.div.removeEventListener("pointercancel", up);
      };
      this.div.addEventListener("pointermove", move);
      this.div.addEventListener("pointerup", up);
      this.div.addEventListener("pointercancel", up);
    }
  }

  const pin = new Pin();
  pin.setMap(map);

  // ── State of what's drawn ─────────────────────────────────────────────────
  let outlinePoly: any = null;
  let outlineShape: Outline | null = null;
  let rotate: RotateHandle | null = null;
  let stalk: any = null;
  let trimShapes: any[] = [];
  let trimHandles: any[] = [];
  let trimGuard = false;
  let scanShapes: any[] = [];
  let scanFrame = 0;
  let panelPolys: any[] = [];

  const ll = (p: LL) => new g.maps.LatLng(p.lat, p.lng);
  const path = (pts: LL[]) => pts.map(ll);

  // ── Outline (§7.3): accent stroke 3, fill 0.12, draggable; a faint frame in adjust mode ──
  function drawOutline(corners: LL[], o: Outline | null, adjust: boolean) {
    clearHighlight();
    outlineShape = o;
    outlinePoly = new g.maps.Polygon({
      map,
      paths: path(corners),
      strokeColor: c.accent,
      strokeWeight: 3,
      strokeOpacity: adjust ? 0.5 : 1,
      fillColor: c.accent,
      fillOpacity: adjust ? 0 : 0.12,
      draggable: !adjust,
      clickable: !adjust,
      zIndex: 2,
    });
    if (!adjust) {
      outlinePoly.addListener("dragstart", () => {
        pin.hide();
        if (rotate) rotate.div.hidden = true;
        if (stalk) stalk.setMap(null);
      });
      outlinePoly.addListener("dragend", () => {
        const pts: any[] = outlinePoly.getPath().getArray();
        const lat = pts.reduce((s, p) => s + p.lat(), 0) / pts.length;
        const lng = pts.reduce((s, p) => s + p.lng(), 0) / pts.length;
        events.onOutlineMoved({ lat, lng });
      });
    }
    // The rotate handle exists only in adjust mode, and only for a fitted outline
    if (adjust && o) {
      rotate = new RotateHandle(events.onRotate, () => outlineShape?.c ?? null);
      rotate.setMap(map);
      placeRotate(o);
    }
  }

  /** Redraw the outline for a new heading (rotate drag), keeping the handle on its end. */
  function updateOutline(o: Outline) {
    outlineShape = o;
    if (outlinePoly) outlinePoly.setPath(path(outlineCorners(o)));
    if (rotate) placeRotate(o);
  }

  function placeRotate(o: Outline) {
    const end = offset(o.c, o.len / 2, o.heading);
    const handleAt = offset(o.c, o.len / 2 + 2.0, o.heading);
    rotate?.place(handleAt);
    if (stalk) stalk.setPath(path([end, handleAt]));
    else stalk = new g.maps.Polyline({ map, path: path([end, handleAt]), strokeColor: c.accent, strokeWeight: 3, clickable: false, zIndex: 3 });
  }

  function clearHighlight() {
    outlinePoly?.setMap(null);
    outlinePoly = null;
    rotate?.setMap(null);
    rotate = null;
    stalk?.setMap(null);
    stalk = null;
  }

  // ── Trim (§5.6, §7.4): dimmed ends, the slice, two white handles ───────────
  function drawTrim(geo: RowGeometry, trim: Trim) {
    // Dimmed ends and the slice are rebuilt; handles are created once and moved
    trimShapes.forEach((s) => s.setMap(null));
    const dim = (a: number, b: number) =>
      new g.maps.Polygon({
        map,
        paths: path([geo.at(a, 0), geo.at(b, 0), geo.at(b, 1), geo.at(a, 1)]),
        fillColor: "#000000",
        fillOpacity: 0.45,
        strokeOpacity: 0,
        clickable: false, // a tap reaches the map and re-centres the slice
        zIndex: 1,
      });
    trimShapes = [];
    if (trim.t0 > 0) trimShapes.push(dim(0, trim.t0));
    if (trim.t1 < 1) trimShapes.push(dim(trim.t1, 1));
    trimShapes.push(
      new g.maps.Polygon({
        map,
        paths: path(trimCorners(geo, trim)),
        strokeColor: c.accent,
        strokeWeight: 3,
        fillColor: c.accent,
        fillOpacity: 0.15,
        clickable: false,
        zIndex: 2,
      }),
    );
    const at0 = geo.at(trim.t0, 0.5);
    const at1 = geo.at(trim.t1, 0.5);
    trimGuard = true; // moving the handles onto the centre line must not re-trigger the drag handler
    if (!trimHandles.length) {
      trimHandles = (["t0", "t1"] as const).map((h, i) => {
        const circle = new g.maps.Circle({
          map,
          center: ll(i === 0 ? at0 : at1),
          radius: 0.9,
          draggable: true,
          fillColor: "#FFFFFF",
          fillOpacity: 1,
          strokeColor: c.accent,
          strokeWeight: 3,
          zIndex: 10,
        });
        circle.addListener("center_changed", () => {
          if (trimGuard) return;
          const cen = circle.getCenter();
          events.onTrimHandle(h, geo.tOf({ lat: cen.lat(), lng: cen.lng() }));
        });
        return circle;
      });
    } else {
      trimHandles[0].setCenter(ll(at0));
      trimHandles[1].setCenter(ll(at1));
    }
    trimGuard = false;
  }

  /** Remove the trim overlays (the trim values themselves are kept by the caller: the scan reads them). */
  function clearTrim() {
    trimShapes.forEach((s) => s.setMap(null));
    trimShapes = [];
    trimHandles.forEach((h) => h.setMap(null));
    trimHandles = [];
  }

  // ── Scan sweep (§5.7): outline, trailing fill and a white line, two passes ──
  function runScan(corners: [LL, LL, LL, LL], onProgress: (fraction: number) => void): Promise<void> {
    clearScanOverlays();
    const [tl, tr, br, bl] = corners;
    const frame = new g.maps.Polygon({ map, paths: path(corners), strokeColor: c.accent, strokeWeight: 2, fillColor: c.accent, fillOpacity: 0.06, clickable: false, zIndex: 2 });
    const trail = new g.maps.Polygon({ map, paths: path([tl, tr, tr, tl]), strokeOpacity: 0, fillColor: c.accent, fillOpacity: 0.18, clickable: false, zIndex: 3 });
    const line = new g.maps.Polyline({ map, path: path([tl, tr]), strokeColor: "#FFFFFF", strokeWeight: 4, clickable: false, zIndex: 4 });
    scanShapes = [frame, trail, line];
    const start = performance.now();
    return new Promise((resolve) => {
      const step = (now: number) => {
        const elapsed = now - start;
        const pass = elapsed < PASS_MS ? 0 : 1;
        const p = Math.min(1, (elapsed - pass * PASS_MS) / PASS_MS);
        const frac = pass === 0 ? easeInOut(p) : 1 - easeInOut(p);
        const left = interpolate(tl, bl, frac);
        const right = interpolate(tr, br, frac);
        line.setPath(path([left, right]));
        trail.setPath(path([tl, tr, right, left]));
        onProgress(Math.min(1, elapsed / MIN_SCAN_MS));
        if (elapsed < MIN_SCAN_MS) scanFrame = requestAnimationFrame(step);
        else resolve();
      };
      scanFrame = requestAnimationFrame(step);
    });
  }

  function clearScanOverlays() {
    cancelAnimationFrame(scanFrame);
    scanShapes.forEach((s) => s.setMap(null));
    scanShapes = [];
  }

  // ── Panels (§7.4): one polygon per offered panel, revealed best-first ───────
  // The corners come from the server, already laid out and foreshortened by the pitch.
  function buildPanels(panels: { corners: LL[] }[]) {
    clearPanels();
    panelPolys = panels.map(
      (p) =>
        new g.maps.Polygon({
          map,
          paths: path(p.corners),
          fillColor: PANEL_FILL,
          fillOpacity: 0.92,
          strokeColor: PANEL_STROKE,
          strokeOpacity: 0.95,
          strokeWeight: 1,
          clickable: false,
          visible: false,
          zIndex: 5,
        }),
    );
  }

  /** Show the first n panels (best first), hide the rest. */
  function showPanels(n: number) {
    panelPolys.forEach((poly, i) => poly.setVisible(i < n));
  }

  /** Reveal panels one by one up to n (70 ms apart). Resolves when done; cancelled by clearPanels. */
  let revealTimer = 0;
  function revealPanels(n: number): Promise<void> {
    window.clearInterval(revealTimer);
    let i = 0;
    return new Promise((resolve) => {
      if (n <= 0) return resolve();
      revealTimer = window.setInterval(() => {
        panelPolys[i]?.setVisible(true);
        i++;
        if (i >= n) {
          window.clearInterval(revealTimer);
          resolve();
        }
      }, PANEL_REVEAL_MS);
    });
  }

  function clearPanels() {
    window.clearInterval(revealTimer);
    panelPolys.forEach((p) => p.setMap(null));
    panelPolys = [];
  }

  return {
    pin,
    drawOutline,
    updateOutline,
    clearHighlight,
    drawTrim,
    clearTrim,
    runScan,
    clearScanOverlays,
    buildPanels,
    showPanels,
    revealPanels,
    clearPanels,
    /** New branding colours: restyle the HTML overlays (shapes are redrawn by the caller). */
    setColors(next: OverlayColors) {
      c = next;
      setVar(pin.div);
      if (rotate) setVar(rotate.div);
    },
    clearAll() {
      clearPanels();
      clearScanOverlays();
      clearHighlight();
      clearTrim();
    },
  };
}

export type Overlays = ReturnType<typeof createOverlays>;
